import React, { useRef, useEffect, useState } from 'react';

const COMBINATIONS = [
  { id: 'proton', label: 'Proton', quarks: [{ q: 'u', color: 'red' }, { q: 'u', color: 'green' }, { q: 'd', color: 'blue' }], charge: '+1', valid: true, name: 'p' },
  { id: 'neutron', label: 'Neutron', quarks: [{ q: 'u', color: 'red' }, { q: 'd', color: 'green' }, { q: 'd', color: 'blue' }], charge: '0', valid: true, name: 'n' },
  { id: 'pion', label: 'Pion (π⁺)', quarks: [{ q: 'u', color: 'red' }, { q: 'd̄', color: 'anti-red' }], charge: '+1', valid: true, name: 'π⁺', isMeson: true },
  { id: 'invalid', label: 'Invalid (two red)', quarks: [{ q: 'u', color: 'red' }, { q: 'u', color: 'red' }, { q: 'd', color: 'blue' }], charge: '+1', valid: false, name: '✕' },
];

const COLOR_MAP = {
  red: '#ff4444',
  green: '#44cc44',
  blue: '#4488ff',
  'anti-red': '#44ffff',
  'anti-green': '#ff44ff',
  'anti-blue': '#ffcc44',
};

const COLOR_LABELS = { red: 'Red', green: 'Green', blue: 'Blue', 'anti-red': 'Anti-red', 'anti-green': 'Anti-green', 'anti-blue': 'Anti-blue' };

export default function QuarkColorCharge({ mode }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const [selected, setSelected] = useState(COMBINATIONS[0]);
  const [showExplanation, setShowExplanation] = useState(false);
  const isRuach = mode === 'ruach';
  const accentColor = isRuach ? '#ffb060' : '#f0c040';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const cx = W / 2, cy = H / 2;
    let t = 0;

    const draw = () => {
      t += 0.018;
      ctx.clearRect(0, 0, W, H);

      const combo = selected;
      const quarks = combo.quarks;
      const count = quarks.length;
      const r = count === 2 ? 55 : 65;

      // Color mixing visualization — center glow
      if (combo.valid) {
        const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, 40);
        if (count === 3) {
          // RGB → white
          gradient.addColorStop(0, 'rgba(255,255,255,0.35)');
          gradient.addColorStop(0.5, 'rgba(255,255,255,0.15)');
          gradient.addColorStop(1, 'rgba(255,255,255,0)');
        } else {
          // color + anticolor → white
          gradient.addColorStop(0, 'rgba(255,255,255,0.3)');
          gradient.addColorStop(1, 'rgba(255,255,255,0)');
        }
        ctx.beginPath(); ctx.arc(cx, cy, 40, 0, Math.PI * 2);
        ctx.fillStyle = gradient; ctx.fill();
      }

      // Gluon field lines between quarks
      quarks.forEach((q1, i) => {
        quarks.forEach((q2, j) => {
          if (j <= i) return;
          const a1 = (i / count) * Math.PI * 2 + t * 0.3;
          const a2 = (j / count) * Math.PI * 2 + t * 0.3;
          const x1 = cx + Math.cos(a1) * r * 0.5;
          const y1 = cy + Math.sin(a1) * r * 0.5;
          const x2 = cx + Math.cos(a2) * r * 0.5;
          const y2 = cy + Math.sin(a2) * r * 0.5;

          ctx.beginPath();
          const cp1x = cx + Math.cos((a1 + a2) / 2 + 0.4) * 25;
          const cp1y = cy + Math.sin((a1 + a2) / 2 + 0.4) * 25;
          ctx.moveTo(x1, y1);
          ctx.quadraticCurveTo(cp1x, cp1y, x2, y2);
          ctx.strokeStyle = `rgba(224,96,255,${0.35 + 0.15 * Math.sin(t * 2 + i)})`;
          ctx.lineWidth = 1.5; ctx.stroke();
        });
      });

      // Quarks
      quarks.forEach((q, i) => {
        const angle = (i / count) * Math.PI * 2 - Math.PI / 2 + t * (combo.valid ? 0.25 : 0);
        const px = cx + Math.cos(angle) * r;
        const py = cy + Math.sin(angle) * r;
        const qColor = COLOR_MAP[q.color];

        // Glow
        const grd = ctx.createRadialGradient(px, py, 0, px, py, 20);
        grd.addColorStop(0, qColor + '50'); grd.addColorStop(1, qColor + '00');
        ctx.beginPath(); ctx.arc(px, py, 20, 0, Math.PI * 2);
        ctx.fillStyle = grd; ctx.fill();

        ctx.beginPath(); ctx.arc(px, py, 13, 0, Math.PI * 2);
        ctx.fillStyle = qColor + (combo.valid ? 'dd' : '66');
        ctx.fill();

        if (!combo.valid) {
          ctx.beginPath(); ctx.arc(px, py, 13, 0, Math.PI * 2);
          ctx.strokeStyle = '#ff4444'; ctx.lineWidth = 2;
          ctx.setLineDash([3, 3]); ctx.stroke(); ctx.setLineDash([]);
        }

        ctx.font = 'bold 10px Space Mono, monospace';
        ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(q.q, px, py);

        // Color label
        ctx.font = '8px Space Mono, monospace';
        ctx.fillStyle = qColor; ctx.globalAlpha = 0.8;
        ctx.fillText(COLOR_LABELS[q.color], px, py + 22);
        ctx.globalAlpha = 1;
      });

      // Center label
      ctx.font = 'bold 13px Space Mono, monospace';
      ctx.fillStyle = combo.valid ? '#fff' : '#ff4444';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(combo.name, cx, cy);

      if (combo.valid) {
        ctx.font = '9px Space Mono, monospace';
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        ctx.fillText('colorless', cx, cy + 16);
      } else {
        ctx.font = '9px Space Mono, monospace';
        ctx.fillStyle = '#ff4444';
        ctx.fillText('forbidden', cx, cy + 16);
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [selected]);

  return (
    <div>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', letterSpacing: '0.1em', marginBottom: '1.25rem', fontFamily: 'Space Mono', textTransform: 'uppercase' }}>
        {isRuach ? 'Three must become one — color confinement' : 'QCD color charge — why quarks combine'}
      </p>

      {/* Selector */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {COMBINATIONS.map(c => (
          <button key={c.id} onClick={() => setSelected(c)} style={{
            padding: '5px 12px', borderRadius: '6px', cursor: 'pointer',
            background: selected.id === c.id ? (c.valid ? `${accentColor}20` : 'rgba(255,68,68,0.15)') : 'rgba(255,255,255,0.04)',
            border: `1px solid ${selected.id === c.id ? (c.valid ? accentColor + '50' : '#ff444450') : 'rgba(255,255,255,0.1)'}`,
            color: selected.id === c.id ? (c.valid ? accentColor : '#ff4444') : 'rgba(255,255,255,0.5)',
            fontFamily: 'Space Mono', fontSize: '0.72rem', transition: 'all 0.15s',
          }}>
            {c.label}
          </button>
        ))}
      </div>

      <canvas ref={canvasRef} width={280} height={220}
        style={{ width: '100%', maxWidth: '280px', display: 'block', margin: '0 auto' }} />

      {/* Info */}
      <div style={{ marginTop: '1.25rem', borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}>
        <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {selected.quarks.map((q, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: COLOR_MAP[q.color] }} />
              <span style={{ fontFamily: 'Space Mono', fontSize: '0.75rem', color: COLOR_MAP[q.color] }}>{q.q}</span>
            </div>
          ))}
          <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', marginLeft: 'auto', fontFamily: 'Space Mono' }}>
            Charge: {selected.charge}
          </span>
          <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: '20px', fontFamily: 'Space Mono', background: selected.valid ? 'rgba(80,224,160,0.15)' : 'rgba(255,68,68,0.15)', color: selected.valid ? '#50e0a0' : '#ff4444' }}>
            {selected.valid ? 'Allowed' : 'Forbidden'}
          </span>
        </div>

        <div style={{ padding: '1rem' }}>
          <p style={{ fontSize: '0.83rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, margin: 0 }}>
            {isRuach
              ? selected.valid
                ? selected.id === 'proton'
                  ? '"Hear, O Israel: The LORD our God, the LORD is one." — Deuteronomy 6:4. Three quarks, three different colors — red, green, blue — together form white: colorless, unified, complete. The proton requires all three primary colors simultaneously. Three become one. The Shema resonates in the structure of matter itself.'
                  : selected.id === 'pion'
                  ? 'A meson — one quark and one antiquark, carrying color and anticolor. Two opposites that together dissolve into colorlessness. The color and its mirror, unified. "There is neither... for you are all one." — Galatians 3:28.'
                  : 'Three utterances in different colors — together colorless, together complete. The hidden rule of creation: no color may stand alone; three must become one white.'
                : 'Two quarks of the same color — this is forbidden. Nature does not permit two of the same color in a baryon. The rule is absolute: three different colors must unite, or color and anticolor must cancel. Uniformity of color is not allowed.'
              : selected.valid
                ? selected.id === 'proton'
                  ? 'Three quarks each carrying a different color charge (red, green, blue) combine to form a "colorless" proton. Color confinement means only colorless combinations can exist as free particles. Red + green + blue = white = colorless.'
                  : selected.id === 'pion'
                  ? 'A meson containing one quark and one antiquark. Color + anticolor = colorless. Mesons are unstable — they eventually decay, unlike the stable proton.'
                  : 'Valid three-quark combination. Red + green + blue produces a colorless hadron — the only type of combination the strong force permits to exist freely.'
                : 'Two quarks with the same color charge — this violates color confinement. Nature forbids this combination. The strong force always demands that the total color charge of any free particle sum to zero (colorless).'
            }
          </p>
        </div>
      </div>

      {/* Color rule summary */}
      <div style={{ marginTop: '1rem', padding: '0.875rem 1rem', borderRadius: '8px', border: `1px solid ${accentColor}25`, background: `${accentColor}08` }}>
        <div style={{ fontSize: '0.62rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.1em', color: accentColor, marginBottom: '0.5rem' }}>
          {isRuach ? 'The rule of binding' : 'Color confinement rule'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {[
            { rule: 'Red + Green + Blue = Colorless ✓', note: isRuach ? 'Three utterances, all different — one baryon' : 'Baryons (proton, neutron)' },
            { rule: 'Color + Anticolor = Colorless ✓', note: isRuach ? 'A color and its mirror — one meson' : 'Mesons (pions, kaons)' },
            { rule: 'Same color + Same color = Forbidden ✗', note: isRuach ? 'Uniformity is not permitted' : 'Violates color confinement' },
          ].map((r, i) => (
            <div key={i} style={{ fontSize: '0.75rem', color: r.rule.includes('✗') ? 'rgba(255,100,100,0.7)' : 'rgba(255,255,255,0.55)', fontFamily: 'Space Mono', display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <span>{r.rule}</span>
              <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem', flexShrink: 0 }}>{r.note}</span>
            </div>
          ))}
        </div>
        {isRuach && (
          <p style={{ fontSize: '0.75rem', color: `${accentColor}70`, fontStyle: 'italic', margin: '0.75rem 0 0', lineHeight: 1.6 }}>
            "For where two or three gather in my name, there am I with them." — Matthew 18:20. The minimum gathering. Three colors must be present — all different — for matter to be stable.
          </p>
        )}
      </div>
    </div>
  );
}
